import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  Link,
  Checkbox,
  FormControlLabel,
  Divider,
  Dialog,
  styled,
  DialogContent,
  } from '@mui/material';
import './login.css';

// Image paths from public folder
const mainImg = '/images/loginimage.png';
const mailIcon = '/images/mail.png';
const lockIcon = '/images/unlock.png';
const hiddenIcon = '/images/hidden.png';
const eyeIcon = '/images/eye.png';
const appleicon = '/images/apple-logo.png';

// Styled components (define at top-level to avoid re-creation on every render)
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    padding: theme.spacing(2),
    maxWidth: '450px',
    width: '100%',
  },
}));

/**
 * @typedef {Object} LoginFormValues
 * @property {string} email
 * @property {string} password
 */

const SignUpPage = () => {
  const isSmallScreen = useMediaQuery('(max-width: 900px)');
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState(null);

  const [open, setOpen] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [canResend, setCanResend] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [verificationType, setVerificationType] = useState('');
  const otpRefs = useRef([...Array(6)].map(() => React.createRef()));
  
  // Handle OTP resend timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [timeLeft]);
  
  const handleOpen = (type = 'email') => {
    setVerificationType(type);
    setOpen(true);
    // Reset OTP and timer when opening
    setOtp(['', '', '', '', '', '']);
    setTimeLeft(30);
    setCanResend(false);
  };
  
  const handleClose = () => {
    setOpen(false);
    setLoginError(null);
  };


  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow numbers
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus to next input
    if (value && index < 5) {
      otpRefs.current[index + 1].current.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Move focus to previous input on backspace
      otpRefs.current[index - 1].current.focus();
    }
  };

  // Handle OTP resend
  const handleResend = () => {
    // Reset OTP and timer
    setOtp(['', '', '', '', '', '']);
    setTimeLeft(30);
    setCanResend(false);
    
    // Focus on first OTP input
    if (otpRefs.current[0]?.current) {
      otpRefs.current[0].current.focus();
    }
    
    // Add your resend OTP logic here based on verificationType
    console.log(`Resending OTP for ${verificationType}...`);
  };

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Handle OTP verification
  const handleVerify = () => {
    const enteredOtp = otp.join('');
    if (enteredOtp.length !== 6) {
      setLoginError('Please enter a valid 6-digit OTP');
      return;
    }
    
    console.log(`Verifying OTP for ${verificationType}:`, enteredOtp);
    
    // In a real app, you would make an API call to verify the OTP
    // For demo, we'll simulate a successful verification
    setTimeout(() => {
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('authProvider', verificationType);
      handleClose();
      navigate('/dashboard');
    }, 1000);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleGoogleResponse = useCallback((response) => {
    console.log("Google Sign-In response:", response);
    // In a real app, you would verify the token with your backend
    // For demo, we'll show the OTP popup
    handleOpen('google');
  }, []);

  const initializeGoogleSignIn = useCallback(() => {
    if (window.google && window.google.accounts && window.google.accounts.id) {
      window.google.accounts.id.initialize({
        client_id: "YOUR_GOOGLE_CLIENT_ID", // Replace with your actual client ID
        callback: handleGoogleResponse,
      });

      window.google.accounts.id.renderButton(
        document.getElementById("googleSignInDiv"),
        {
          theme: "outline",
          size: "large",
          text: "signin_with",
          shape: "rectangular",
          logo_alignment: "left",
        }
      );
    }
  }, [handleGoogleResponse]);

  // Initialize Google Sign-In
  useEffect(() => {
    // Load Google Sign-In script if not already loaded
    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);

      script.onload = () => {
        initializeGoogleSignIn();
      };
    } else {
      initializeGoogleSignIn();
    }
  }, [initializeGoogleSignIn]);

  // Apple Login Functions
  const handleAppleLogin = () => {
    // In a real app, you would implement Apple Sign In flow here
    // For demo, we'll show the OTP popup
    handleOpen('apple');
  };

  // Google Login Function (show OTP popup similar to LinkedIn demo)
  const handleGoogleLogin = () => {
    // In a real app, you would start Google OAuth flow here
    // For demo, we'll show the OTP popup
    handleOpen('google');
  };

  const [password, setPassword] = useState("");

const ruleList = [
  password.length >= 8,
  /[A-Z]/.test(password),
  /[a-z]/.test(password),
  /[0-9]/.test(password),
  /[@!#$]/.test(password),
];

const passedCount = ruleList.filter(Boolean).length;
const allValid = passedCount === ruleList.length;

  const exchangeCodeForToken = useCallback((code) => {
    // In a real application, this should be done on your backend
    // for security reasons (to protect your client secret)

    // For demo purposes, we'll simulate a successful login
    console.log("Exchanging code for token:", code);

    // Simulate API call
    setTimeout(() => {
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('authProvider', 'linkedin');
      navigate('/dashboard');
    }, 1000);
  }, [navigate]);

  // Handle LinkedIn callback (this would typically be in a separate component)
  useEffect(() => {
    // Check if we're on the callback page and have a code parameter
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (code && state) {
      // Verify state to prevent CSRF attacks
      const storedState = localStorage.getItem('linkedin_oauth_state');
      if (state !== storedState) {
        setLoginError('Invalid state parameter');
        return;
      }

      // Clear the state
      localStorage.removeItem('linkedin_oauth_state');

      // Exchange code for access token (this should be done on your backend)
      exchangeCodeForToken(code);
    } else if (error) {
      setLoginError(`LinkedIn login failed: ${error}`);
    }
  }, [exchangeCodeForToken]);

  return (
    <Box className="login-page-wrapper">
      <Box className="login-grid" container alignItems="center" justifyContent={'center'}>
        {/* Image Column - Hidden on small screens */}
        {!isSmallScreen && (
          <Box className="img-text child left" md={6}>
            <Box className="login-image-container">
    {/* Back to website */}
    <Box className="back-to-website">
       Back to Login  <span className="arrow">→</span>
    </Box>

  <Box
    component="img"
    src={mainImg}
    alt="Main"
    className="signup-main-image"
  />



            </Box>
          </Box>
        )}

        {/* Login Form Column */}
        <Box item xs={12} md={6}>
          <Box className="login-form-container child right ">
            <Box elevation={3} className="login-paper">
              <Box className="login-header">
                <Typography variant="h4" component="h1" className="login-title">
                  Create Your Account
                </Typography>
                <Typography variant="h6" component="h6" className="login-sub-title">
                  Join us today
                </Typography>
              </Box>

              <form  className="login-form">
                {/* Email Field */}
                <Box className="form-field-container-login sp-mb">
                  <Typography className="label-inputss">Email Id</Typography>
                  <TextField
                    className='form-field-signup'
                    fullWidth
                    id="email"
                    name="email"
                    

                  
                  />
                </Box>

                {/* Password Field */}
  <Box className="form-field-container-login sp-mb">
      <Typography className="label-inputss">Password</Typography>

      <TextField
        fullWidth
        type="password"
        className="form-field-signup"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />

      {/* small boxes */}
<Box className="password-rules">
  {ruleList.map((_, index) => (
    <span
      key={index}
      className={`rule-box ${index < passedCount ? "active" : ""}`}
    />
  ))}
</Box>

<Typography
  className={`password-hint ${allValid ? "all-valid" : ""}`}
>
  Use at least 8 characters with mix of uppercase, lowercase, number and
  special symbol (@, !, #, $)
</Typography>


    </Box>
      <Box className="form-field-container-login sp-mb">
      <Typography className="label-inputss"> Confirm Password</Typography>

      <TextField
        fullWidth
        type="password"
         className="form-field-signup"
      />


    </Box>
                

                {/* Error Message */}
                {loginError && (
                  <Typography color="error" sx={{ mt: 2 }}>
                    {loginError}
                  </Typography>
                )}

                <Box className="signup-container">
                  {/* SIGN UP heading */}
<label className="terms-checkbox">
  <input
    type="checkbox"
    className="custom-checkbox"
  />
  <Typography variant="body2" className="terms-label" component="span">
    I Agree to the{" "}
    <Link href="#" underline="always" className="forgot-link">
      Terms and Conditions
    </Link>
  </Typography>
</label>

                              <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                className="login-button"
                              >
                                Signup
                              </Button>
              
          <Box className="divider-container">
                    <Divider className="divider-line" />
                    <Typography className="divider-text">Or continue with</Typography>
                    <Divider className="divider-line" />
                  </Box>


                  <Box className="social-buttons">
                    <Button
                      variant="outlined"
                      className="google-btn social-btn"
                      onClick={handleGoogleLogin}
                    >
                      <img src="/images/google.png" alt="Google" className="google-icon" />
                      Sign in with Google
                    </Button>

                    <Button
                      variant="outlined"
                      className="google-btn social-btn"
                      onClick={handleAppleLogin}
                    >
                      <img src="/images/apple-logo.png" alt="Apple" className="social-icon" />
                      Sign in with Apple
                    </Button>
                  </Box>
                </Box>
              </form>
            </Box>
          </Box>
        </Box>
        </Box>
      <StyledDialog open={open} onClose={handleClose} fullWidth keepMounted className="otp-dialog">
        <DialogContent className="otp-dialog-content">
          <img src="/images/otp.png" alt="OTP Security" className="otp-security-icon" />

          <Typography variant="h5" component="h2" className="otp-heading">
            Verify Your Identity
          </Typography>

          <Typography variant="body1" className="otp-subtext">
            We've sent a 6 digit code to your Email ID
          </Typography>

          <Box className="otp-input-row-login">
            {otp.map((digit, index) => (
              <TextField
                key={index}
                className="otp-input"
                inputRef={otpRefs.current[index]}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                inputProps={{ maxLength: 1 }}
                variant="outlined"
              />
            ))}
          </Box>

          {loginError && (
            <Typography color="error" sx={{ textAlign: 'center', mb: 2 }}>
              {loginError}
            </Typography>
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleVerify}
            className="otp-verify-btn"
          >
            Verify Code
          </Button>

          <Box>
            {!canResend ? (
              <Typography variant="body2" className="otp-timer-text">
                You can resend the code in{" "}
                <span className="otp-timer">{formatTime(timeLeft)}</span>
              </Typography>
            ) : (
              <Typography variant="body2" className="otp-resend-text">
                Didn't receive the code?
                <Button
                  variant="text"
                  size="small"
                  className="otp-resend-btn"
                  onClick={handleResend}
                >
                  Resend
                </Button>
              </Typography>
            )}
          </Box>
        </DialogContent>
      </StyledDialog>
    </Box>
  );
};
export default SignUpPage;